import type { RejectCode } from '@mendikot/engine';
import type { z } from 'zod';
import type { commandMetaSchema, commandPayloads } from './schemas';

/** Every command the callable accepts (kept in sync with `commandPayloads` by the type below). */
export const COMMAND_TYPES = [
  'room.create',
  'room.join',
  'room.leave',
  'room.configure',
  'seat.take',
  'seat.ready',
  'seat.addBot',
  'seat.removeBot',
  'match.start',
  'trump.reserve',
  'card.play',
  'trump.revealRequest',
  'turn.claimTimeout',
  'turn.nudge',
  'hand.next',
  'player.reconnect',
  'rematch.vote',
  'profile.mergeGuest',
  'account.delete',
  'push.syncTopics',
] as const satisfies readonly (keyof typeof commandPayloads)[];

export type CommandType = keyof typeof commandPayloads;
export type CommandPayload<T extends CommandType> = z.input<(typeof commandPayloads)[T]>;

/** Commands that act on a specific turn and must carry the version they were issued against (T11). */
export const TURN_SCOPED: ReadonlySet<CommandType> = new Set<CommandType>([
  'trump.reserve',
  'card.play',
  'trump.revealRequest',
  'turn.claimTimeout',
]);

/** Commands that run against a room's core document (everything except account-level ones). */
export const ROOM_COMMANDS: ReadonlySet<CommandType> = new Set<CommandType>(
  COMMAND_TYPES.filter(
    (t): t is CommandType =>
      !['room.create', 'profile.mergeGuest', 'account.delete', 'push.syncTopics'].includes(t),
  ),
);

export type CommandMeta = z.infer<typeof commandMetaSchema>;

export interface Envelope<T extends CommandType = CommandType> {
  type: T;
  roomId?: string;
  code?: string;
  expectedVersion?: number;
  payload: CommandPayload<T>;
  meta: CommandMeta;
}

export type HostErrorCode =
  | 'NOT_FOUND'
  | 'NOT_MEMBER'
  | 'NOT_HOST'
  | 'ROOM_FULL'
  | 'ROOM_IN_PROGRESS'
  | 'ROOM_CLOSED'
  | 'SEAT_TAKEN'
  | 'BAD_SEAT'
  | 'SEATS_EMPTY'
  | 'NOT_READY'
  | 'STALE_VERSION'
  | 'INVALID_RULES'
  | 'TOO_EARLY'
  | 'ALREADY_RESOLVED'
  | 'NOT_AWAY'
  | 'RATE_LIMITED'
  | 'INVALID_ARGUMENT'
  | 'UNAUTHENTICATED'
  | 'NO_SUCH_CODE'
  | 'UNKNOWN_COMMAND'
  | 'INTERNAL';

export type ErrorCode = RejectCode | HostErrorCode;

export type Reply =
  | { ok: true; version: number; data?: Record<string, unknown> }
  | { ok: false; code: ErrorCode; message?: string };

// Compile-time check: COMMAND_TYPES covers every payload schema.
type _MissingCommands = Exclude<keyof typeof commandPayloads, (typeof COMMAND_TYPES)[number]>;
const _allCommands: _MissingCommands extends never ? true : false = true;
void _allCommands;
